/* UserDataStorageFactory.js */

'use strict';

const EventEmitter = require('events'),
    UserRemoteStorage = require('./DataStorage/UserRemoteStorage'),
    EntityGridUserStorage = require('./DataStorage/EntityGridUserStorage'),
    UserSessionStorage = require('./DataStorage/UserSessionStorage'),
    UserLocalStorage = require('./DataStorage/UserLocalStorage'),
    _ = require('lodash');

/**
 *  Factory service to store user data in the database or browser storage,
 *  class is initialized after user login with loadUserPreferences().
 *  To use - inject it with steve and call .getRemoteStorageInstance(storageRootKey, storageKey),
 *  or .getLocalStorageInstance() if you want to store data in the database or browserstorage,
 *  or .getSessionStorageInstance(storageRootName, storageKey), to store data, in the sessionStorage
 *
 *  Class have 3 states:
 *  isNotInitialized(this._isInitialized = false), when class just created,
 *  isInitialized(this._isInitialized = true), when loadUserPreferences() started, and
 *  isReady(this._isReady = true), when loadUserPreferences() completes, and storageFactory is ready to work
 *
 *  To make sure storageFactory is ready, use whenStorageIsReady() method,
 *  it checks initialization, and wait, until all data will be retrieved from backend side
 */
class UserDataStorageFactory extends EventEmitter {
    constructor(logger, userService, userSession) {
        super();
        this._logger = logger;

        // Steve user services - Only required for remote storage
        this._userService = userService;
        this._userSession = userSession;
        this._userId = undefined;

        this._isInitialized = false;
        this._isReady = false;
        this._storePreferences = undefined;
        this._updatePreferences = undefined;
        this._userPreferences = undefined;
    }

    // eslint-disable-next-line no-unused-vars
    initialize(steve, addOnOptions) {
        this._registerListeners();
        return Promise.resolve();
    }

    whenStorageIsReady() {
        /*
        const self = this;

        function sleep(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        async function waitForReady() { // jshint ignore:line
            if (self._isReady === true) {
                Promise.resolve();
            } else {
                let count = 0;
                while (self._isReady === false && count < 10) {
                    count += 1;
                    await sleep(500); // jshint ignore:line
                }
                if (count >= 10) {
                    return Promise.reject("Can't load DataStorage");
                }
                return Promise.resolve();
            }
        }*/

        return new Promise((resolve, reject) => {
            try {
                if (this._isInitialized === false) {
                    return this.loadUserPreferences();
                } else if (this._isReady === false) {
                    //return waitForReady();

                    this.once("StorageReady", resolve);
                } else {
                    resolve();
                }
            } catch(error) {
                reject(error);
            }
        });
    }

    _registerListeners() {
        this._userSession.on('Authenticated', this._onAuthenticated = this._onAuthenticated.bind(this));
    }

    _unregisterListeners() {
        this._userSession.removeListener('Authenticated', this._onAuthenticated);
    }

    shutdown() {
        //this.removeGlobalResetMethod();
        return new Promise((resolve) => {
            this._unregisterListeners();
            this._userService = undefined;
            this._userSession = undefined;
            this._userId = undefined;
            this._storePreferences = undefined;
            this._updatePreferences = undefined;
            this._userPreferences = undefined;
            this._logger = undefined;
            this._isInitialized = undefined;
            this._isReady = undefined;
            return resolve();
        });
    }

    _onAuthenticated() {
        this.loadUserPreferences();
    }

    /**
     * The storage factory initialization function
     **/
    loadUserPreferences() {
        const currentUserId = _.get(this._userSession, "_userEntity.id", undefined);
        // make sure we use preferences of the current user
        if (this._isInitialized === false || this._userId !== currentUserId) {
            this._isInitialized = true; //make sure we initiate userPreferences only once
            this._userId = currentUserId;

            return this._userService.retrievePreferences({ userId: this._userId })
                .then((result) => {
                    this._storePreferences = this._userService.storePreferences;
                    this._updatePreferences = this._userService.updatePreferences;
                    this._userPreferences = result || {};
                    this._isReady = true;
                    this.emit('StorageReady');
                    return Promise.resolve();
                })
                .catch((err) => {
                    this._isInitialized = false;
                    return Promise.reject(err);
                });
        } else {
            return Promise.resolve();
        }
    }

    clearPreferences(userId)
    {
        return this._userService.clearPreferences(userId)
            .then(()=>{
                //clear immediate refs to prefs if the user to clear is myself
                if(this._userId === userId) {
                    this._userPreferences = {};
                }
                return Promise.resolve();
            })
            .catch( (err) => {
                return Promise.reject(err);
            });
    }

    /**
     * Creates a local storage instance from root name, and key provided.
     * @param {String} storageRootName
     * @param {String} storageKey
     */
    getRemoteStorageInstance(storageRootName, storageKey) {

        if (!this._userPreferences) {
            return this.storageFallback(`UserDataStorageFactory: Remote storage didn't initialized yet, nor user was not authenticated.`);
        }

        try {
            return this.createLocalInstance(storageRootName, storageKey, UserRemoteStorage);
        } catch (err) {
            return this.storageFallback(`UserDataStorageFactory: Remote storage cannot be loaded: ${err.message}`);
        }
    }

    /**
     * Creates a local storage instance for entity_grid_storage from root name, and key provided.
     * @param {String} storageRootName
     * @param {String} storageKey
     */
    getRemoteStorageInstanceForEntityGrid(storageRootName, storageKey) {

        if (!this._userPreferences) {
            return this.storageFallback(`UserDataStorageFactory: Remote storage didn't initialized yet, nor user was not authenticated.`);
        }

        try {
            return this.createLocalInstance(storageRootName, storageKey, EntityGridUserStorage);
        } catch (err) {
            return this.storageFallback(`UserDataStorageFactory: Remote storage cannot be loaded: ${err.message}`);
        }
    }

    /**
     * Creates a window.localStorage instance
     * @param {String} storageRootName
     * @param {String} storageKey
     */
    getLocalStorageInstance() {
        return new UserLocalStorage(this._logger, this._userSession, this._userId);
    }

    /**
     * Creates a window.sessionStorage instance
     * @param {String} storageRootName
     * @param {String} storageKey
     */
    getSessionStorageInstance(storageRootName, storageKey) {
        return new UserSessionStorage(storageRootName, storageKey, this._userSession, this._logger);
    }

    /**
     * Creates a local storage instance from root name, and key provided. UserRemoteStorage can be extended, and attached as 3-rd parameter
     * @param {String} storageRootName
     * @param {String} storageKey
     * @param {Object=} userStorageClass user storage class, UserRemoteStorage class, or extend from it
     */
    createLocalInstance(storageRootName, storageKey, userStorageClass = UserRemoteStorage) {
        let storageInstance = new userStorageClass(storageRootName, storageKey, this._userSession, this._logger);
        this._userPreferences[storageRootName] = this._userPreferences[storageRootName] || {};
        let storageRemoteData = this._userPreferences[storageRootName];

        let saveItem = (data) => {
            data = _.isUndefined(data) ? storageRemoteData : data;

            let request = {
                userId: this._userId,
                prefObj: {
                    [storageRootName]: {}
                }
            };
            storageRemoteData = data;
            request.prefObj[storageRootName] = JSON.parse(JSON.stringify(data));
            this._storePreferences(request)
                .catch((err) => {
                    this._logger.error({ error: err }, err, 'UserDataStorageFactory: storePreferences - Error');
                    return Promise.reject(err);
                });
        };

        const updateItem = (key, value = "", operator = '$set') => {
            if (!key) {
                return Promise.resolve({});
            }
            return this._updatePreferences({
                    userId: this._userId,
                    operator: operator,
                    docKey: `${storageRootName}.${key}`,
                    docValue: value
                })
                .catch((err) => {
                    this._logger.error({ error: err }, err, `UserDataStorageFactory: Update ${key} Preferences Error`);
                    return err;
                });
        };

        const setItem = (() => {
            let updateItemPromise = Promise.resolve();

            return (key, value) => {
                storageRemoteData = _.set(storageRemoteData, key, value);
                updateItemPromise = updateItemPromise.then(() => updateItem(key, value));
                return updateItemPromise;
            };
        })();

        const getItem = key => {
            return storageRemoteData[key];
        };

        const removeItem = key => {
            updateItem(key, '', '$unset');
        };

        const resetData = () => {
            saveItem({});
        };

        let storage = {
            setItem: setItem,
            getItem: getItem,
            removeItem: removeItem,
            resetData: resetData
        };

        storageInstance._storage = storage;
        storageInstance._storageData = storageInstance.getStorageData(storageKey);

        return storageInstance;
    }

    storageFallback(errMsg) {
        window.gridStorage = window.gridStorage || {};

        const storage = window.gridStorage;
        const setItem = (key, value) => {
            storage[key] = value;
        };
        const getItem = key => storage[key];
        const removeItem = key => {
            delete storage[key];
        };

        if (errMsg) {
            this._logger.error({ error: errMsg }, `UserDataStorageFactory: ${errMsg}`);
        }

        return {
            setItem: setItem,
            getItem: getItem,
            removeItem: removeItem
        };
    }

    /*
    setGlobalResetMethod(storage, storageName) {
        const resetGridData = window.resetGridData = window.resetGridData || {};

        resetGridData[storageName] = () => {
            storage.removeItem(storageName);
        };

        if (storage.resetData) {
            resetGridData.resetRemoteData = () => storage.resetData();
        }
    }
    removeGlobalResetMethod() {
        if (window.resetGridData) {
            window.resetGridData = _.omit(window.resetGridData);
        }
    }*/
}

module.exports = UserDataStorageFactory;
module.exports.$inject = ['$teve.logger', '$teve.services.User', '$teve.userSession'];
